/**
 * ManageLanguagesModal
 *
 * Configure which languages a screener or panel sign-up form is offered in.
 *
 * Model:
 *   - The first ("Default") row is the primary/source content — the
 *     language-agnostic `form` JSON you build in the editor, used as a fallback
 *     whenever a respondent's language has no translation. The primary is not
 *     tied to any language; the author may *optionally* declare which language
 *     it is written in, stored as `screener_translation.default_lang`. Setting
 *     or changing it never touches any form content or translations, and it is
 *     left unset (no assumed default) until the author picks one.
 *   - Each additional row is a translation language whose overlay lives in
 *     `screener_translation.lang[code]`. Changing a row's language re-keys the
 *     overlay (preserving its content and list position); removing a row drops
 *     that overlay.
 *   - Languages are mutually exclusive across rows — a code already used by one
 *     row is hidden from the others' pickers.
 *
 * @typedef {import('utils/surveyTranslations').ScreenerTranslation} ScreenerTranslation
 */

import React, { useEffect, useState } from 'react';
import { Button } from 'rebass';
import { Label } from '@rebass/forms';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import NiceModal from 'components/NiceModal';
import { Combobox } from 'components/Combobox';
import { findLanguageMeta, getDefaultLang, listSupportedLanguages } from 'utils/surveyTranslations';

/**
 * Translation language codes configured on this tree (excludes the default).
 *
 * @param {ScreenerTranslation} translation
 * @returns {string[]}
 */
function getTranslationCodes(translation) {
    return Object.keys((translation && translation.lang) || {});
}

/**
 * One row in the "Enabled languages" list: a fixed position label, a searchable
 * language picker, and an optional remove button. Rows without `onRemove` (the
 * Default row) reserve the trash-button width so every picker stays aligned.
 *
 * @param {Object} props
 * @param {string} props.rowLabel       e.g. "Default" or "Language 2"
 * @param {string} props.comboId        unique DOM id for the picker
 * @param {string} props.value          currently-selected language code ('' while picking)
 * @param {Array<{ value: string, label: string }>} props.items
 * @param {(code: string) => void} props.onChange
 * @param {() => void} [props.onRemove]
 * @param {string} [props.removeAriaLabel]
 */
function LanguageRow({ rowLabel, comboId, value, items, onChange, onRemove, removeAriaLabel }) {
    return (
        <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-medium w-[96px] shrink-0">{rowLabel}</div>
            <div className="grow">
                <Combobox id={comboId} ariaLabel={rowLabel} items={items} value={value} onChange={onChange} />
            </div>
            {onRemove ? (
                <Button
                    type="button"
                    variant="secondary-gray"
                    className="secondary-icon"
                    onClick={onRemove}
                    mr={0}
                    aria-label={removeAriaLabel}
                >
                    <FiTrash2 />
                </Button>
            ) : (
                <div className="w-[34px] shrink-0" aria-hidden="true" />
            )}
        </div>
    );
}

/**
 * Modal: manage which languages the form is offered in.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {ScreenerTranslation} props.screener_translation
 * @param {(next: ScreenerTranslation) => void} props.onChange
 */
function ManageLanguagesModal({ isOpen, onClose, screener_translation, onChange }) {
    // Transient UI state: the in-progress "Add language" picker row. Reset when
    // the modal closes so reopening always starts from the saved configuration.
    const [isAdding, setIsAdding] = useState(false);
    const translation = screener_translation || {};
    // Optional — `null`/`''` means the author hasn't declared the primary's
    // language. We never assume one (the primary content is language-agnostic).
    const defaultLang = getDefaultLang(translation) || '';
    const translationCodes = getTranslationCodes(translation);
    const usedCodes = [defaultLang, ...translationCodes].filter(Boolean);

    const supported = listSupportedLanguages();
    // Picker items for an existing row: every supported language except those
    // claimed by *other* rows (the row's own code stays selectable).
    const itemsForRow = ownCode =>
        supported
            .filter(l => l.code === ownCode || !usedCodes.includes(l.code))
            .map(l => ({ value: l.code, label: l.label }));
    // Picker items for the not-yet-assigned "Add language" row: only unused codes.
    const unusedItems = () =>
        supported.filter(l => !usedCodes.includes(l.code)).map(l => ({ value: l.code, label: l.label }));
    // The Default row's picker leads with an explicit "Not specified" choice so
    // an undeclared primary reads as an intentional optional state (rather than
    // the generic "Select an option" empty-field look), and stays clearable.
    const defaultRowItems = [{ value: '', label: 'Not specified' }, ...itemsForRow(defaultLang)];

    // Change the default/primary language. Relabels the source language only —
    // the Primary `form` content and every translation overlay stay intact.
    // Picking "Not specified" (empty) clears the declared language.
    const handleDefaultChange = nextCode => {
        const code = nextCode || '';
        if (code === defaultLang) return;
        const next = { ...translation, lang: { ...(translation.lang || {}) } };
        if (code) {
            next.default_lang = code;
            // Defensive: an overlay for the new default would be redundant (the
            // default's content is the Primary form), so drop it if one exists.
            delete next.lang[code];
        } else {
            delete next.default_lang;
        }
        onChange(next);
    };

    // Re-key a translation overlay to a different language code, preserving both
    // its contents and its position in the list (object key insertion order).
    // Leaves `default_lang` exactly as-is — never introduces an assumed primary.
    const handleTranslationCodeChange = (oldCode, nextCode) => {
        if (!nextCode || nextCode === oldCode) return;
        const prevLang = translation.lang || {};
        const rebuilt = {};
        Object.keys(prevLang).forEach(code => {
            rebuilt[code === oldCode ? nextCode : code] = prevLang[code];
        });
        onChange({ ...translation, lang: rebuilt });
    };

    const handleAdd = code => {
        if (!code) return;
        // Spread `translation` as-is so we never write a `default_lang` the
        // author didn't pick — the primary stays language-agnostic.
        const next = { ...translation, lang: { ...(translation.lang || {}) } };
        next.lang[code] = next.lang[code] || { questions: {}, ui: {} };
        setIsAdding(false);
        onChange(next);
    };

    const handleRemove = code => {
        const next = { ...translation, lang: { ...(translation.lang || {}) } };
        delete next.lang[code];
        onChange(next);
    };

    useEffect(() => {
        if (!isOpen) setIsAdding(false);
    }, [isOpen]);

    const canAddMore = unusedItems().length > 0;

    if (!isOpen) return null;

    return (
        <NiceModal
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            onRequestClose={onClose}
            style={{ content: { width: '510px' } }}
            title="Manage Languages"
        >
            <div className="text-sm color-text-secondary mb-3">
                Add the languages this form is offered in. The <strong>Default</strong> is the content you build in the
                editor; naming its language is optional. It&rsquo;s shown whenever a translation is missing.
            </div>

            <Label mt={3} mb={2}>
                Enabled languages
            </Label>

            <LanguageRow
                rowLabel="Default"
                comboId="manage-languages-default"
                value={defaultLang}
                items={defaultRowItems}
                onChange={handleDefaultChange}
            />

            {translationCodes.map((code, i) => (
                <LanguageRow
                    key={code}
                    rowLabel={`Language ${i + 2}`}
                    comboId={`manage-languages-${code}`}
                    value={code}
                    items={itemsForRow(code)}
                    onChange={next => handleTranslationCodeChange(code, next)}
                    onRemove={() => handleRemove(code)}
                    removeAriaLabel={`Remove ${findLanguageMeta(code).label}`}
                />
            ))}

            {isAdding && (
                <LanguageRow
                    rowLabel={`Language ${translationCodes.length + 2}`}
                    comboId="manage-languages-add"
                    value=""
                    items={unusedItems()}
                    onChange={handleAdd}
                    onRemove={() => setIsAdding(false)}
                    removeAriaLabel="Cancel adding language"
                />
            )}

            {!isAdding && canAddMore && (
                <Button type="button" variant="secondary-gray" mt={2} onClick={() => setIsAdding(true)}>
                    <FiPlus style={{ marginRight: '8px' }} aria-hidden="true" />
                    Add language
                </Button>
            )}

            <div className="modal-actions">
                <Button type="button" variant="primary" mr={0} className="modal-primary" onClick={onClose}>
                    Done
                </Button>
            </div>
        </NiceModal>
    );
}

export default ManageLanguagesModal;
